import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  Ticket, TicketsSummary, ListTicketsFilters, CreateTicketInput, UpdateTicketInput, TicketMessage,
  listTickets, getTicket, createTicket, updateTicket, addTicketMessage, getTicketsSummary,
} from '../services/ticketsApi';

interface TicketsState {
  items: Ticket[];
  current: Ticket | null;
  summary: TicketsSummary | null;
  pagination: { total: number; page: number; limit: number; pages: number } | null;
  filters: ListTicketsFilters;
  loading: boolean;
  saving: boolean;
  error: string | null;
  setFilters: (patch: Partial<ListTicketsFilters>) => void;
  resetFilters: () => void;
  fetchList: () => Promise<void>;
  fetchOne: (id: number | string) => Promise<Ticket>;
  fetchSummary: () => Promise<void>;
  create: (input: CreateTicketInput) => Promise<Ticket>;
  update: (id: number | string, input: UpdateTicketInput) => Promise<Ticket>;
  reply: (id: number | string, body: string, isInternal?: boolean) => Promise<TicketMessage>;
}

const defaultFilters: ListTicketsFilters = { page: 1, limit: 25 };
const toErr = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong');

export const useTicketsStore = create<TicketsState>()(
  devtools(
    (set, get) => ({
      items: [],
      current: null,
      summary: null,
      pagination: null,
      filters: { ...defaultFilters },
      loading: false,
      saving: false,
      error: null,
      setFilters: (patch) => set({ filters: { ...get().filters, ...patch, page: 1 } }),
      resetFilters: () => set({ filters: { ...defaultFilters } }),
      fetchList: async () => {
        set({ loading: true, error: null });
        try {
          const result = await listTickets(get().filters);
          set({ items: result.data, pagination: result.pagination, loading: false });
        } catch (err) {
          set({ loading: false, error: toErr(err) });
        }
      },
      fetchOne: async (id) => {
        set({ loading: true, error: null });
        try {
          const ticket = await getTicket(id);
          set({ current: ticket, loading: false });
          return ticket;
        } catch (err) {
          set({ loading: false, error: toErr(err) });
          throw err;
        }
      },
      fetchSummary: async () => {
        try {
          const summary = await getTicketsSummary();
          set({ summary });
        } catch (err) {
          set({ error: toErr(err) });
        }
      },
      create: async (input) => {
        set({ saving: true, error: null });
        try {
          const created = await createTicket(input);
          set((s) => ({ items: [created, ...s.items], saving: false }));
          return created;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      update: async (id, input) => {
        set({ saving: true, error: null });
        try {
          const updated = await updateTicket(id, input);
          set((s) => ({
            items: s.items.map((t) => (String(t.id) === String(id) ? updated : t)),
            current: s.current && String(s.current.id) === String(id) ? updated : s.current,
            saving: false,
          }));
          return updated;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
      reply: async (id, body, isInternal) => {
        set({ saving: true, error: null });
        try {
          const message = await addTicketMessage(id, body, isInternal);
          set((s) => ({
            current:
              s.current && String(s.current.id) === String(id)
                ? { ...s.current, messages: [...(s.current.messages ?? []), message] }
                : s.current,
            saving: false,
          }));
          return message;
        } catch (err) {
          set({ saving: false, error: toErr(err) });
          throw err;
        }
      },
    }),
    { name: 'tickets-store' }
  )
);
